/* eslint semi: 1 */
import { isString } from 'lodash';
import { t } from '@/i18n/i18ns';
import { asyncFormValidation, asyncValidationRule } from '@/element-ui/form-utils';

import { proxyException, PageResponse, EitherResponse } from '../player-setup-communication';
import PlayerSetupFormError from '../player-setup-form-error/player-setup-form-error-tmpl';
import OnEnterMixin from '@/components/mixins/onenter-mixin';
import { useMixpanel } from '@base-shared/composables/use-mixpanel';

/**
 * Function to remove any non-ascii characters and spaces
 */
export const cleanupSerial = (value) => {
  return value.replace(/[^A-Za-z0-9-]*/g, '');
};

export const validateSerial = asyncValidationRule(async (rule, value) => {
  if (value.replace(/[\s]*/g, '') === '') {
    return new Error(t('playerSetup.incorrectSerialNumber'));
  }
  if (cleanupSerial(value) !== value.replace(/[\s]*/g, '')) {
    return new Error(t('playerSetup.incorrectSerialNumberCharacters'));
  }
});

export default {
  name: 'player-setup-serial',
  inject: [
    '$utils',
    '$api',
    '$apiError'
  ],
  components: {
    PlayerSetupFormError
  },
  mixins: [OnEnterMixin],
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    serial: {
      type: String,
      default: ''
    },
    externalError: {
      type: String,
      default: null
    }
  },
  watch: {
    serial: {
      immediate: true,
      handler (val) {
        this.model.serial = val;
      }
    },
    externalError: {
      immediate: true,
      handler (val) {
        if (val) {
          this.formError = val;
        }
      }
    }
  },
  data () {
    return {
      isFormDisabled: false,
      formError: '',
      attemptsCounter: {},
      model: {
        serial: ''
      },
      modelRules: {
        serial: [
          { validator: validateSerial, trigger: 'blur' }
        ]
      }
    };
  },
  mounted () {
    if (this.isActive) {
      this.refreshForm();
    }
  },
  methods: {
    /**
     * Remove validation results from the screen and focus primary input
     */
    refreshForm () {
      this.formError = '';
      this.$refs.refFormSerial.clearValidate();
      this.$refs.serialInput.focus();
    },
    /**
     * Display "Serial Number not correct" on first error,
     * "Connect to the Internet. Connect Power." on second error with same serial number,
     * "Contact support." on third or more error.
     */
    getAttemptErrorMessage (attempt) {
      switch (attempt) {
        case 1:
          return this.$t('label.playerIsNotConnectedToTheCloud');
        case 2:
          return this.$t('label.checkThePlayer');
        default:
          return this.$t('label.invalidUserdata');
      }
    },
    /**
     * Verifies the Serial Number of a player via an API.
     * It displays a loader while waiting for the response and handles
     * different error scenarios by returning appropriate error messages.
     * Returns an EitherResponse object with two field: ok and error respectively.
     */
    async verifySerial (serial) {
      const url = `${CONFIG.API_BASE_URL}/player/${serial}`;

      // reset counters when other serial number has been entered
      if (!this.attemptsCounter[serial]) {
        this.attemptsCounter = { [serial]: 0 };
      }

      // display loader and perform HTTP request
      const { ok: response, error } = await proxyException(() => this.$api.get(url, { params: { addIntent: 'arya' } }));

      // error scenarios
      //  || (typeof (response?.data?.status) !== 'string'
      if (error || !response?.data) {
        let errorMessage = this.$t('label.invalidUserdata');
        switch (error?.response?.status) {
          case 404:
            this.attemptsCounter[serial] += 1;
            errorMessage = this.getAttemptErrorMessage(this.attemptsCounter[serial]);
            break;
          case 403:
            errorMessage = this.$t('label.serialNumberAlreadyAssigned');
            break;
          case 412:
          case 409:
            // put specific message when there's any on the server side
            if (error.response.data && error.response.data.name === 'ExceededMaximumPlayersError') {
              errorMessage = error.response.data.description;
            }
            break;
          case 401:
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { this.submitForm(); } });
            return EitherResponse(null, errorMessage);
          default:
            if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            }
            break;
        }

        return EitherResponse(null, errorMessage);
      }

      // all good branch
      useMixpanel().track('Player found');
      return EitherResponse(response.data);
    },
    toggleFormState (idDisabled) {
      this.isFormDisabled = idDisabled;
    },
    toggleNetworkLoader (isEnabled) {
      this.$utils.mainLoader(isEnabled);
    },
    /**
     * This is pattern to perform form validation and easily to test it
     */
    async validateForm (validateFn = asyncFormValidation) {
      this.toggleFormState(true);
      const isFormValid = await validateFn(this.$refs.refFormSerial);
      this.toggleFormState(false);
      return isFormValid;
    },
    async submitForm () {
      const isFormValid = await this.validateForm();

      if (!isFormValid) {
        return PageResponse(true);
      }

      const serial = cleanupSerial(this.model.serial);

      this.toggleNetworkLoader(true);
      const { ok: response, error } = await this.verifySerial(serial);
      this.toggleNetworkLoader(false);

      if (error) {
        // reset validation/ set focus
        this.formError = error;
        this.$refs.serialInput.focus();
        return PageResponse(true);
      }

      // we'll override originally entered serial number with the one from the server
      return PageResponse(false, null, {
        serial: response.serialNumber,
        isNewPlayer: !isString(response.status),
        isAryaPlayer: !!response.inArya
      });
    },
    onEnter (event) {
      this.triggerFormSubmit();
    },
    async triggerFormSubmit (event) {
      this.$emit('SubmitForm');
      return false;
    }
  }
};
